import axios from 'axios';
import { BASE_URL } from './helpers';
import { TrainingDataset } from './loraRuntime';

/**
 * Request to the vision-adjust endpoint: a vision-capable model compares the
 * reference image(s) against a generated candidate and rewrites the generation
 * prompt to better match the influencer's identity.
 */
export interface VisionAdjustRequest {
  reference_media_ids: string[];
  candidate_media_id: string;
  prompt: string;
  feedback?: string;
}

export interface VisionAdjustResponse {
  adjusted_prompt: string;
  analysis: string;
}

export interface CreateDatasetFromMediaRequest {
  name: string;
  subject_token: string;
  media_ids: string[];
}

export interface AddMediaToDatasetRequest {
  media_ids: string[];
}

const createDatasetFromMedia = async (
  req: CreateDatasetFromMediaRequest
): Promise<TrainingDataset> => {
  const res = await axios.post<TrainingDataset>(`${BASE_URL}/datasets/from-media`, req);
  return res.data;
};

const addMediaToDataset = async (
  datasetId: string,
  req: AddMediaToDatasetRequest
): Promise<TrainingDataset> => {
  const res = await axios.post<TrainingDataset>(
    `${BASE_URL}/datasets/${encodeURIComponent(datasetId)}/from-media`,
    req
  );
  return res.data;
};

/**
 * Asks the backend to critique a candidate image against the reference image(s)
 * and return an improved generation prompt. Lives on the protected api group
 * behind RequireFeature("generate"); shares the BASE_URL + axios convention of
 * the other api modules.
 */
const visionAdjust = async (req: VisionAdjustRequest): Promise<VisionAdjustResponse> => {
  const res = await axios.post<VisionAdjustResponse>(`${BASE_URL}/datasets/vision-adjust`, req);
  return res.data;
};

/**
 * Request to attach an approved review image as a character reference. The
 * server resolves `media_id` → url (media store), downloads the bytes
 * server-side (avoiding browser CORS on signed urls), and forwards them to the
 * bridge's addCharacterReferences. `url` is an optional fallback the server can
 * use when it cannot resolve the media id.
 */
export interface AddCharacterReferenceFromMediaRequest {
  media_id: string;
  url?: string;
  /**
   * When true, attaches the image to the character's `reference_image_refs`
   * ONLY — no trainable dataset item is appended. Set this for
   * reference-collage-generator runs, whose approved collages are references,
   * not training-dataset images (unlike a dataset run's approvals).
   */
  skip_dataset_item?: boolean;
}

/**
 * Attaches an approved review image to a character profile by media id,
 * server-side. By default the backend appends the image as one item to the
 * character's persistent TrainingDataset (creating + binding it on first
 * approval) and returns that dataset, whose `image_count` is the true
 * approved-image count training reads. Pass `skip_dataset_item: true` to
 * attach as a reference only (no dataset item), e.g. for collage runs.
 */
const addCharacterReferenceFromMedia = async (
  characterId: string,
  req: AddCharacterReferenceFromMediaRequest
): Promise<TrainingDataset> => {
  const res = await axios.post<TrainingDataset>(
    `${BASE_URL}/datasets/characters/${characterId}/references/from-media`,
    req
  );
  return res.data;
};

const DatasetsAPI = {
  visionAdjust,
  addCharacterReferenceFromMedia,
  createDatasetFromMedia,
  addMediaToDataset,
};
export default DatasetsAPI;
